import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, downloadAuthed } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

function toLocalInput(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function labelSlot(iso) {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export default function ScheduleForm({ jobId, initial, onDone }) {
  const [start, setStart] = useState(toLocalInput(initial?.start));
  const [duration, setDuration] = useState(initial?.duration_minutes || 120);
  const [crew, setCrew] = useState(initial?.crew || "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [notify, setNotify] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [busy, setBusy] = useState(false);
  const [conflicts, setConflicts] = useState([]);

  useEffect(() => {
    let cancel = false;
    api.get("/schedule/next-slot", { params: { duration_minutes: duration || 120 } })
      .then((response) => {
        if (cancel) return;
        setSuggestion(response.data);
        if (response.data?.start) setStart((current) => current || toLocalInput(response.data.start));
      })
      .catch(() => {});
    return () => { cancel = true; };
  }, [duration, initial?.start]);

  const save = async () => {
    if (!start) { toast.error("Pick a date and time"); return; }
    setBusy(true);
    try {
      const response = await api.post(`/jobs/${jobId}/schedule`, {
        start: new Date(start).toISOString(),
        duration_minutes: Number(duration) || 120,
        crew,
        notes,
        notify_client: notify,
      });
      setConflicts(response.data.conflicts || []);
      toast.success(notify && response.data.client_email_sent ? "Scheduled and the client was emailed" : "Job scheduled");
      if (onDone) onDone(response.data.schedule);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Couldn't schedule that");
    }
    setBusy(false);
  };

  const downloadIcs = async () => {
    try {
      await downloadAuthed(`/jobs/${jobId}/calendar.ics`, "job.ics");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Schedule the job first");
    }
  };

  return (
    <div className="space-y-3" data-testid="schedule-form">
      {suggestion?.start && (
        <button
          type="button"
          data-testid="suggest-slot"
          onClick={() => setStart(toLocalInput(suggestion.start))}
          className="w-full text-left bg-orange-50 border border-orange-100 rounded-xl px-3 py-2"
        >
          <p className="text-[10px] uppercase font-bold text-orange-700">Next open slot</p>
          <p className="text-sm font-semibold text-zinc-900">{labelSlot(suggestion.start)} · {suggestion.duration_minutes} min</p>
        </button>
      )}
      <label className="block">
        <span className="text-[11px] uppercase font-bold text-zinc-400">Start</span>
        <Input data-testid="schedule-start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="h-12 mt-1" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase font-bold text-zinc-400">Duration (minutes)</span>
        <Input data-testid="schedule-duration" type="number" min="15" step="15" value={duration} onChange={(e) => setDuration(e.target.value)} className="h-12 mt-1" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase font-bold text-zinc-400">Crew</span>
        <Input data-testid="schedule-crew" value={crew} onChange={(e) => setCrew(e.target.value)} placeholder="Optional" className="h-12 mt-1" />
      </label>
      <label className="block">
        <span className="text-[11px] uppercase font-bold text-zinc-400">Notes</span>
        <Textarea data-testid="schedule-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-1" placeholder="Gate code, parking, what to bring" />
      </label>
      <button
        type="button"
        data-testid="schedule-notify"
        onClick={() => setNotify((value) => !value)}
        className={`w-full h-11 rounded-xl text-sm font-bold ${notify ? "bg-orange-600 text-white" : "bg-zinc-100 text-zinc-700"}`}
      >
        {notify ? "Email the client a confirmation" : "Don't email the client"}
      </button>
      {conflicts.length > 0 && (
        <p data-testid="schedule-conflicts" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
          Overlaps {conflicts.map((row) => row.customer_name || "another job").join(", ")}. Saved anyway.
        </p>
      )}
      <Button data-testid="schedule-save" onClick={save} disabled={busy} className="w-full h-12 font-bold">
        {busy ? "Saving…" : "Save schedule"}
      </Button>
      <Button data-testid="add-to-calendar" type="button" variant="outline" onClick={downloadIcs} className="w-full h-11 font-bold">
        Add to my calendar
      </Button>
    </div>
  );
}
